/**
 * Enterprise workflow task model (Phase 1).
 * Persistence: TaskPersistence → localStorage (swap for API without changing callers).
 *
 * @typedef {'pending' | 'in_progress' | 'blocked' | 'completed' | 'overdue' | 'cancelled'} TaskStatus
 * @typedef {'admission' | 'settling' | 'clinical' | 'finance' | 'hr' | 'quality'} TaskCategory
 * @typedef {'system' | 'manual'} TaskSource
 *
 * @typedef {Object} WorkflowTask
 * @property {string} id
 * @property {string} facilityId
 * @property {string|number} residentId
 * @property {string|null} [enquiryId]
 * @property {string} title
 * @property {string} [description]
 * @property {TaskCategory} category
 * @property {string} [ownerRole] — e.g. Admissions, RN, Finance
 * @property {string|null} [assigneeUserId]
 * @property {string|null} [dueAt] — ISO date (date-only or datetime)
 * @property {'low'|'normal'|'high'|'critical'} priority
 * @property {TaskStatus} status
 * @property {string|null} [blockedReason]
 * @property {TaskSource} source
 * @property {string} [templateId] — id from checklist template
 * @property {string|null} [workflowKey] — e.g. stage6_admission
 * @property {string|null} [completedAt]
 * @property {string|null} [completedBy]
 * @property {string|null} [evidenceNote]
 * @property {string[]} [dependsOnTemplateIds] — templateIds that must be completed before this task unlocks
 * @property {number} version
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} [createdBy]
 */

export const TASK_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  COMPLETED: 'completed',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled',
};

export const TASK_CATEGORY = {
  ADMISSION: 'admission',
  SETTLING: 'settling',
  CLINICAL: 'clinical',
  FINANCE: 'finance',
  HR: 'hr',
  QUALITY: 'quality',
};

export const TASK_SOURCE = {
  SYSTEM: 'system',
  MANUAL: 'manual',
};

/** Roles used for filtering / future RBAC (demo: display only). */
export const OWNER_ROLES = {
  ADMISSIONS: 'Admissions',
  RN: 'RN',
  CLINICAL: 'Clinical',
  FINANCE: 'Finance',
  IT: 'IT',
  CARE: 'Care staff',
  ADMIN: 'Admin',
  KITCHEN: 'Kitchen',
  FACILITY_MANAGER: 'Facility Manager',
};

/**
 * @param {Partial<WorkflowTask>} t
 * @returns {WorkflowTask}
 */
export function normalizeTask(t) {
  const now = new Date().toISOString();
  return {
    id: t.id || `task_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    facilityId: t.facilityId ?? 'unknown',
    residentId: t.residentId,
    enquiryId: t.enquiryId ?? null,
    title: t.title ?? 'Untitled task',
    description: t.description ?? '',
    category: t.category ?? TASK_CATEGORY.ADMISSION,
    ownerRole: t.ownerRole,
    assigneeUserId: t.assigneeUserId ?? null,
    dueAt: t.dueAt ?? null,
    priority: t.priority ?? 'normal',
    status: t.status ?? TASK_STATUS.PENDING,
    blockedReason: t.blockedReason ?? null,
    source: t.source ?? TASK_SOURCE.SYSTEM,
    templateId: t.templateId,
    workflowKey: t.workflowKey ?? null,
    dependsOnTemplateIds: Array.isArray(t.dependsOnTemplateIds) ? t.dependsOnTemplateIds : [],
    completedAt: t.completedAt ?? null,
    completedBy: t.completedBy ?? null,
    evidenceNote: t.evidenceNote ?? null,
    version: t.version ?? 1,
    createdAt: t.createdAt ?? now,
    updatedAt: t.updatedAt ?? now,
    createdBy: t.createdBy ?? 'system',
  };
}
